/**
 * Deleting an account from the browser, in the phone's order: the data first, then the login.
 *
 * The web used to delete the Cognito user and nothing else. `DELETE /me/account` — the purge the
 * phone runs first — had no caller here, so a person told "this removes your account and the apps
 * you have built" kept every app and record on the server, and lost the only credential that could
 * ever ask for them to be removed. The order and the four outcomes are the phone's own
 * (`runAccountDeletion`, src/account/deleteAccount.ts), shared rather than copied.
 */
import { runAccountDeletion, type DeletionOutcome } from '@/account/deleteAccount';
import { request } from '../../api/client';
import { deleteAccount as closeLogin } from '../../api/auth';

/**
 * A purge walks every app the person owns, so it is given the gateway's whole 29 seconds rather
 * than an ordinary call's 15: timing out here would report "nothing was deleted" about a purge
 * that is still running.
 */
const PURGE_TIMEOUT_MS = 29_000;

export function purgeAccountData(): Promise<{ deleted?: number; visitorDataSkipped?: boolean }> {
  return request<{ deleted?: number; visitorDataSkipped?: boolean }>('/me/account', {
    method: 'DELETE',
    timeoutMs: PURGE_TIMEOUT_MS,
  });
}

/**
 * Runs both halves. The login is closed through the auth module directly, and the session has no
 * delete of its own, on purpose: clearing the signed-in user here would let the route guard swap
 * this screen for Sign in before the outcome can be read — which would hide "some of your data
 * could not be reached" from the one person who needs to know. The screen signs out itself once
 * the outcome has been shown.
 */
export function deleteAccountEverywhere(deps: {
  purge?: typeof purgeAccountData;
  deleteAuth?: () => Promise<void>;
} = {}): Promise<DeletionOutcome> {
  return runAccountDeletion({
    purge: deps.purge ?? purgeAccountData,
    deleteAuth: deps.deleteAuth ?? closeLogin,
  });
}
